import { getCountryByCode } from "../../../../utils/Helper";

/* Ownership Structure — the rules. */

/* Transcribed: the card's four rows. An owner is either an individual (a
   `name`) or a company (a `company_name`), never both, so the second row's
   label changes with the type — which is exactly what the design shows. */
export const OWNERSHIP_ROWS = [
  { key: "type", label: "Type" },
  { key: "name", label: "Name" },
  { key: "country", label: "Country" },
  { key: "percentage_of_shares_held", label: "Percentage of shares held" },
];

export const isCompanyOwner = (record) =>
  String(record?.type || "").toLowerCase() === "company";

/* Transcribed from createArrayFromObject / createArrayFromObjectDraft, which differ only in which country field they read: the live record keeps the code in... */
export const ownershipRowValues = (record = {}) => {
  const company = isCompanyOwner(record);
  const raw = record?.type ? String(record.type) : "";
  return {
    type: raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "",
    name: company ? record.company_name || record.name || "" : record.name || "",
    nameLabel: company ? "Company name" : "Name",
    country: getCountryByCode(record?.nationality) || "",
    percentage_of_shares_held:
      record?.percentage_of_shares_held === undefined ||
      record?.percentage_of_shares_held === null
        ? ""
        : `${record.percentage_of_shares_held} %`,
  };
};

/* Transcribed: "raised" and "pending" join "Requested" as awaiting review. */
export const ownershipStatus = (record) =>
  ["raised", "pending"].includes(record?.status) ? "Requested" : record?.status;

/* Transcribed: an owner row is live when its own side is undeleted AND the
   other side is null — the join carries one or the other, never both. */
export const liveOwners = (company) =>
  (company?.ownershipCompany || []).filter((row) => {
    const ind = row?.ownershipID?.ownership_indID;
    const comp = row?.ownershipID?.ownership_compID;
    if (ind && comp == null) return Number(ind.is_deleted) === 0;
    if (comp && ind == null) return Number(comp.is_deleted) === 0;
    return false;
  });

/* The owner record out of a join row — whichever side is populated. */
export const ownerOf = (row) =>
  row?.ownershipID?.ownership_indID || row?.ownershipID?.ownership_compID;

/* Transcribed from the reduce in the effect: the running total is rounded to
   two decimals at EVERY step, not once at the end. That matters — three owners
   at 33.333 sum to 100 the live way and 99.999 if rounded only at the end. */
export const totalShares = (records) =>
  (records || []).reduce(
    (sum, value) => parseFloat((sum + (Number(value) || 0)).toFixed(2)),
    0
  );

export const liveTotal = (company) =>
  totalShares(liveOwners(company).map((row) => ownerOf(row)?.percentage_of_shares_held));

export const draftTotal = (rows) =>
  totalShares((rows || []).map((row) => row?.percentage_of_shares_held));

/* The allocation the SECTION shows, which is the one the sidebar has to agree with. */
export const ownershipTotal = (company, draftResponse) =>
  draftTotal(mergeOwnershipDrafts(draftResponse, company));

/* Transcribed from companyStatus(): every draft row, plus any LIVE owner that has no draft of its own — so an owner awaiting review and an untouched one... */
export const mergeOwnershipDrafts = (draftResponse, company) => {
  const ind = draftResponse?.ownership_ind || [];
  const comp = draftResponse?.ownership_com || [];
  const drafts = [...ind, ...comp];

  const draftedIndIds = drafts.map((d) => d?.ownership_indID_id).filter(Boolean);
  const draftedCompIds = drafts.map((d) => d?.ownership_compID_id).filter(Boolean);

  const untouched = [];
  liveOwners(company).forEach((row) => {
    const individual = row?.ownershipID?.ownership_indID;
    const business = row?.ownershipID?.ownership_compID;

    if (business && Number(business.is_deleted) === 0) {
      if (!draftedCompIds.includes(business.ownership_compID)) untouched.push(business);
    }
    if (individual && Number(individual.is_deleted) === 0) {
      if (!draftedIndIds.includes(individual.ownership_indID)) untouched.push(individual);
    }
  });

  return [...drafts, ...untouched];
};

/* Transcribed: a draft row's own identity, used as a React key and as the
   subject of a delete. */
export const ownershipKey = (record) =>
  record?.ownership_ind_draftID ??
  record?.ownership_comp_draftID ??
  record?.ownership_indID ??
  record?.ownership_compID;

/* Transcribed edit-visibility rule, draft rows only: once the COMPANY is
 * Verified, only an individual owner may still be edited — a corporate owner
 * is frozen. Live (non-draft) rows always offer Edit.
 */
export const canEditOwner = ({ companyStatus, record, isDraftRow }) => {
  if (!isDraftRow) return true;
  return companyStatus !== "Verified" || String(record?.type) === "individual";
};

/* Transcribed: Continue is only allowed at 0% or exactly 100%. Anything in
   between opens the "Action Required!" dialog. */
export const canContinue = (total) => total === 0 || total === 100;

/* Transcribed validation, message for message. */
export const validateOwner = ({ type, name, companyName, country, share, total, editing }) => {
  const errors = {};

  if (type === "individual" && String(name ?? "").trim() === "") {
    errors.name = "Name is required.";
  }
  if (!editing && type === "company" && String(companyName ?? "").trim() === "") {
    errors.companyName = "Business Name is required.";
  }
  if (String(country ?? "").trim() === "") {
    errors.country = "Country is required.";
  }

  const raw = String(share ?? "").trim();
  if (raw === "") {
    errors.share = "Ownership Percentage is required.";
    return errors;
  }

  const value = parseFloat(raw);
  if (Number.isNaN(value) || value <= 0 || value > 100) {
    errors.share = "Invalid percentage value. Must be between 0 and 100.";
    return errors;
  }

  if (!editing) {
    const remaining = 100 - total;
    if (value > remaining) {
      errors.share = `Total shares percentage exceeds 100%. You can add up to ${parseFloat(
        remaining.toFixed(2)
      )}%.`;
    }
  } else {
    const allowance = 100 - total + parseFloat(editing.percentage_of_shares_held || 0);
    if (value > allowance) {
      errors.share = `Total shares percentage exceeds 100%. You can allocate up to ${parseFloat(
        allowance.toFixed(2)
      )}% for this owner.`;
    }
  }

  return errors;
};

/* Which of the eight writes a submit performs. */
export const ownershipSavePath = ({ verifiedCompany, editing, type }) => {
  const verified =
    verifiedCompany !== null &&
    verifiedCompany !== undefined &&
    typeof verifiedCompany === "object" &&
    Object.keys(verifiedCompany).length !== 0;

  if (verified) {
    if (editing?.ownership_ind_draftID) return "updateIndDraft";
    if (editing?.ownership_comp_draftID) return "updateCompDraft";
    return type === "individual" ? "createIndDraft" : "createCompDraft";
  }

  if (editing) {
    return editing?.ownership_indID ? "updateInd" : "updateComp";
  }
  return type === "individual" ? "createInd" : "createComp";
};

/* Transcribed: a searched company that exists only in WorldBox has no
   companyID yet, so one is created first and its new id is what the ownership
   row points at. */
export const needsCompanyRecord = (selectedCompany) =>
  Boolean(selectedCompany && !selectedCompany.companyID && selectedCompany.win);
